import React from "react";
import { Link } from "react-router-dom";
import { blogPosts } from "@/data/blogPosts";
import useSEO from "@/hooks/useSEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Hero } from "@/components/seo/SeoLayout";
import { Clock, ArrowRight } from "lucide-react";

const BlogIndexPage = () => {
  useSEO({
    title: "Ecowallet Blog – Sustainable Shopping, AI Sustainability & Greenwashing",
    description: "Deep guides on sustainable shopping, sustainability scores, greenwashing, sustainable alternatives and AI-powered eco intelligence.",
    keywords: "sustainable shopping blog, sustainability guide, greenwashing guide, sustainable products guide",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "Ecowallet Blog",
      description: "Guides on sustainable shopping, AI sustainability scores, greenwashing detection and sustainable alternatives.",
      blogPost: blogPosts.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        datePublished: p.date,
        description: p.description,
        url: (typeof window !== "undefined" ? window.location.origin : "") + "/blog/" + p.slug,
        image: p.hero,
      })),
    },
  });

  const clusters = Array.from(new Set(blogPosts.map((p) => p.cluster)));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24">
      <Breadcrumbs items={[{ label: "Blog" }]} />
      <Hero
        kicker="Ecowallet Blog"
        title="Guides for Sustainable Shoppers"
        subtitle="Deep, practical writing on sustainable shopping, AI sustainability scores, greenwashing detection and finding better green alternatives."
      />

      {/* Topic clusters */}
      <div className="mt-10 flex flex-wrap gap-2">
        {clusters.map((c) => (
          <span key={c} className="rounded-full border border-border px-3 py-1 text-xs uppercase tracking-widest text-muted-foreground">
            {c}
          </span>
        ))}
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-6" data-testid="blog-grid">
        {blogPosts.map((p) => (
          <Link key={p.slug} to={`/blog/${p.slug}`} className="card-premium overflow-hidden hover:-translate-y-1 transition-all flex flex-col" data-testid={`blog-card-${p.slug}`}>
            <div className="aspect-[16/9] overflow-hidden bg-secondary">
              <img src={p.hero} alt="" className="h-full w-full object-cover" loading="lazy" />
            </div>
            <div className="p-6 flex flex-col flex-1">
              <div className="text-[11px] uppercase tracking-widest text-primary font-semibold">{p.cluster}</div>
              <h3 className="mt-2 text-xl font-bold leading-tight">{p.title}</h3>
              <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>
              <div className="mt-auto pt-4 flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {p.read}</span>
                <span className="flex items-center gap-1 text-primary font-semibold">Read <ArrowRight className="h-3 w-3" /></span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default BlogIndexPage;
